import * as _ from 'lodash';
import {
  API,
  Logging,
  NotificationData,
  ResponseError,
  toBoolean,
} from '@vcd-ecm-ui/Common';
import {
  FilterModelApi,
  GenericGridRowData,
  GridDataNativeType,
  GridDataSummaryCollection,
  GridFilter,
  GridSort,
  ReducerContext,
  SortModelApi,
} from '../Interfaces';
import {
  AddFilterAction,
  ADD_FILTER,
  ClearFiltersSortersAction,
  ClearNotificationAction,
  CLEAR_FILTERS_SORTERS,
  CLEAR_NOTIFICATION,
  DataRequestCacheAction,
  DataRequestFailedAction,
  DataRequestStartedAction,
  DataRequestSucceededAction,
  DATA_REQUEST_CACHE,
  DATA_REQUEST_SERVICE_FAILED,
  DATA_REQUEST_SERVICE_STARTED,
  DATA_REQUEST_SERVICE_SUCCEEDED,
  DeleteFilterAction,
  DELETE_FILTER,
  ReportActionTypes,
  SetFiltersSortersAction,
  SetFiltersSortersReadyAction,
  SetNotificationAction,
  SetPaginationFiltersSortersAction,
  SET_FILTERS_SORTERS,
  SET_FILTERS_SORTERS_READY,
  SET_NOTIFICATION,
  SET_PAGINATION_FILTERS_SORTERS,
} from './reportActionTypes';
import { renderFilters, renderSorters } from '../../..';

export const getGridDataServerModel = (
  reducerContext: ReducerContext,
  pageNumber: number,
  pageSize: number,
  filters: GridFilter[],
  sorters: GridSort[],
  bypassCache: boolean,
) => async (): Promise<void> => {
  try {
    // Check if the Filters and Sorting has changed since the last time
    const filtersAndSortingUnchanged: boolean = _.isEqual(reducerContext.state.lastFilters, filters)
      && _.isEqual(reducerContext.state.lastSorters, sorters);

    // Reset the Page Number if the Filtering or Sorting has changed
    let newPageNumber: number = filtersAndSortingUnchanged ? pageNumber : 1; // Page Number is 1 based

    // Limit the Page Number to 1.  The Page Number may be 0 is the Table has not Data and the user toggles the Sorting
    if (newPageNumber < 1) {
      newPageNumber = 1;
    }
    // Calculate the Start and End Row
    const startRow = (newPageNumber - 1) * pageSize; // Page Number is 1 based
    let endRow = startRow + pageSize;

    Logging.Debug('Filters & Sorting:', filtersAndSortingUnchanged, reducerContext.state.lastFilters, filters,
      reducerContext.state.lastSorters, sorters);

    // Use the Cache if Available
    // If Filters or Sorting has changed we need to reload from the Service
    if (!bypassCache && filtersAndSortingUnchanged && reducerContext.state.useCache && reducerContext.state.dataRowsCache) {
      // Limit the endRow if the Total Number of Rows is known - In case where last Page of Cache is requested
      endRow = Math.min(endRow, reducerContext.state.totalNumberRows);

      // Check if requested Rows are in the Cache
      if ((startRow >= reducerContext.state.cacheStartRow) && (endRow <= reducerContext.state.cacheEndRow)) {
        reducerContext.dispatch({
          type: DATA_REQUEST_CACHE,
          pageNumber: newPageNumber,
          pageSize,
          startRow,
          endRow,
        } as DataRequestCacheAction);

        return;
      }
    }

    // Mark the Start of the Request
    reducerContext.dispatch({ type: DATA_REQUEST_SERVICE_STARTED } as DataRequestStartedAction);

    // Calculate the Cache Start and End Row Offsets.  Cache tries to be simmetrical around (Above and Below) the startRow
    const cacheStartRow = (startRow - reducerContext.state.virtualPageSize) < 0 ? 0 : (startRow - reducerContext.state.virtualPageSize);
    const cacheEndRow = startRow + reducerContext.state.virtualPageSize; // Load More Rows than needed

    if (!reducerContext.apiGetRowsServerModel) {
      Logging.Error('The apiGetRowsServerModel method must be specified');
    }

    const collection: GridDataSummaryCollection = await reducerContext.apiGetRowsServerModel(
      reducerContext.state.useCache ? cacheStartRow : startRow,
      reducerContext.state.useCache ? cacheEndRow : endRow,
      renderFilters(filters), // Get the new Style Filter Model
      renderSorters(sorters), // Get the new Style Sorting Model
    );

    // Actual Rows retuned
    const numberRows: number = collection.value.length;

    // Total Rows Available
    const totalNumberRows: number = collection['@odata.count'];

    reducerContext.dispatch({
      type: DATA_REQUEST_SERVICE_SUCCEEDED,
      dataRows: collection.value || [], // Page of Data
      pageNumber: newPageNumber,
      pageSize,
      totalNumberRows, // Total Number of Rows
      startRow,
      endRow: Math.min(endRow, numberRows + startRow), // Reduce the endRow offset if less Rows are available
      cacheStartRow: reducerContext.state.useCache ? cacheStartRow : null,
      // Reduce the cacheEndRow if less Rows are available
      cacheEndRow: reducerContext.state.useCache ? Math.min(cacheEndRow, numberRows + cacheStartRow) : null,
      sorters,
      filters,
    } as DataRequestSucceededAction);
  } catch (error) {
    Logging.Info('getGridDataServerModel', error);

    reducerContext.dispatch({ type: DATA_REQUEST_SERVICE_FAILED, error: error as ResponseError } as DataRequestFailedAction);
  }
};

export const getGridDataClientModel = (
  reducerContext: ReducerContext,
  pageNumber: number,
  pageSize: number,
  filters: GridFilter[],
  sorters: GridSort[],
) => async (): Promise<void> => {
  try {
    // Paging is Ignored for Client Model.  All the Rows are retuned.

    if (!reducerContext.apiGetRowsClientModel) {
      Logging.Error('The apiGetRowsClientModel method must be specified');
    }
    // Mark the Start of the Request
    reducerContext.dispatch({ type: DATA_REQUEST_SERVICE_STARTED } as DataRequestStartedAction);

    // Load all the Possible Rows
    const collection: GridDataSummaryCollection = await reducerContext.apiGetRowsClientModel();

    // Actual Rows retuned
    const numberRows: number = collection.value.length;

    // All Rows was loaded
    let totalNumberRows: number = collection['@odata.count'];

    // Not all the Rows was Read.  All rows should be returned
    if (numberRows !== totalNumberRows) {
      Logging.Warning('All the Rows should be read when using the Client Model:', numberRows, totalNumberRows);
      totalNumberRows = numberRows;
    }

    // The Cache is the Entire set of Data
    const cacheStartRow = 0;
    const cacheEndRow = totalNumberRows;

    reducerContext.dispatch({
      type: DATA_REQUEST_SERVICE_SUCCEEDED,
      dataRows: collection.value || [], // All the Data
      pageNumber,
      pageSize: totalNumberRows, // Change the Page Size to match the Total Rows
      totalNumberRows, // Total Number of Rows
      startRow: cacheStartRow,
      endRow: cacheEndRow,
      cacheStartRow: reducerContext.state.useCache ? cacheStartRow : null,
      cacheEndRow: reducerContext.state.useCache ? cacheEndRow : null,
      sorters,
      filters,
    } as DataRequestSucceededAction);
  } catch (error) {
    Logging.Info('getGridDataClientModel', error);

    reducerContext.dispatch({ type: DATA_REQUEST_SERVICE_FAILED, error: error as ResponseError } as DataRequestFailedAction);
  }
};

export const getGridDataStaticModel = (
  reducerContext: ReducerContext,
  pageNumber: number,
  pageSize: number,
  dataRows: GenericGridRowData[],
  filters: GridFilter[],
  sorters: GridSort[],
) => async (): Promise<void> => {
  try {
    // Paging is Ignored for Client Model.  All the Rows are retuned.

    if (!dataRows) {
      Logging.Error('The dataRows method must be specified');
    }

    // All Rows was loaded
    const totalNumberRows: number = dataRows.length;

    // The Cache is the Entire set of Data
    const cacheStartRow = 0;
    const cacheEndRow = totalNumberRows;

    reducerContext.dispatch({
      type: DATA_REQUEST_SERVICE_SUCCEEDED,
      dataRows, // All the Data
      pageNumber,
      pageSize: totalNumberRows, // Change the Page Size to match the Total Rows
      totalNumberRows, // Total Number of Rows
      startRow: cacheStartRow,
      endRow: cacheEndRow,
      cacheStartRow: reducerContext.state.useCache ? cacheStartRow : null,
      cacheEndRow: reducerContext.state.useCache ? cacheEndRow : null,
      sorters,
      filters,
    } as DataRequestSucceededAction);
  } catch (error) {
    Logging.Info('getGridDataStaticModel', error);

    reducerContext.dispatch({ type: DATA_REQUEST_SERVICE_FAILED, error: error as ResponseError } as DataRequestFailedAction);
  }
};

export const setNotification = (
  reducerContext: ReducerContext,
  notification: NotificationData,
) => async (): Promise<void> => {
  reducerContext.dispatch({ type: SET_NOTIFICATION, notification } as SetNotificationAction);
};

export const clearNotification = (
  reducerContext: ReducerContext,
) => async (): Promise<void> => {
  reducerContext.dispatch({ type: CLEAR_NOTIFICATION } as ClearNotificationAction);
};

export const setFiltersSorters = (
  reducerContext: ReducerContext,
  filters: GridFilter[],
  sorters: GridSort[],
) => async (): Promise<void> => {
  reducerContext.dispatch({ type: SET_FILTERS_SORTERS, filters, sorters } as SetFiltersSortersAction);
};

export const clearFiltersSorters = (
  reducerContext: ReducerContext,
) => async (): Promise<void> => {
  reducerContext.dispatch({ type: CLEAR_FILTERS_SORTERS } as ClearFiltersSortersAction);
};

export const setPaginationFiltersSorters = (
  reducerContext: ReducerContext,
  pageNumber: number,
  pageSize: number,
  filters: GridFilter[],
  sorters: GridSort[],
) => async (): Promise<void> => {
  reducerContext.dispatch({
    type: SET_PAGINATION_FILTERS_SORTERS,
    pageNumber,
    pageSize,
    filters,
    sorters,
  } as SetPaginationFiltersSortersAction);
};

export const addFilter = (
  reducerContext: ReducerContext,
  filter: GridFilter,
) => async (): Promise<void> => {
  reducerContext.dispatch({ type: ADD_FILTER, filter } as AddFilterAction);
};

export const deleteFilter = (
  reducerContext: ReducerContext,
  filter: GridFilter,
) => async (): Promise<void> => {
  reducerContext.dispatch({ type: DELETE_FILTER, filter } as DeleteFilterAction);
};

export const setFiltersSortersReady = (
  reducerContext: ReducerContext,
) => async (): Promise<void> => {
  reducerContext.dispatch({ type: SET_FILTERS_SORTERS_READY } as SetFiltersSortersReadyAction);
};
